import {
  hasOnlyIncompleteTariffs,
  getIncompleteClaimRulesResult,
} from '@billing/validation/incompleteClaim';
import type {
  RuleEvaluationResult,
  RuleViolation,
} from '@billing/validation/rules';
import { useDebouncedValue } from '@mantine/hooks';
import { cloneDeep, isEqual } from 'lodash';
import { evaluateClaimRules } from 'api/resource/claim';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClaimRuleFormData } from './useLiveClaimRules';
import { useRemoteFeatureFlag } from './useRemoteFeatureFlag';
import { normalizeClaimRuleEvaluationData } from 'utils/claimRuleEvaluationUtils';

export interface LiveBulkClaimRulesState {
  evaluation: RuleEvaluationResult | null;
  error: Error | null;
  isChecking: boolean;
  isStale: boolean;
  scheduleEvaluation: (claimsData: ClaimRuleFormData[]) => void;
  setEvaluationBaseline: (
    formValues: { claims: ClaimRuleFormData[] },
    appliedFields?: string[]
  ) => void;
}

const getChangedClaimFields = (
  current: Array<Record<string, unknown>> | null,
  next: Array<Record<string, unknown>>
): string[] => {
  if (!current) return next.map((_, claimIndex) => `claims.${claimIndex}`);

  const changedFields: string[] = [];
  const claimCount = Math.max(current.length, next.length);
  for (let claimIndex = 0; claimIndex < claimCount; claimIndex += 1) {
    const currentClaim = current[claimIndex];
    const nextClaim = next[claimIndex];
    if (!currentClaim || !nextClaim) {
      changedFields.push(`claims.${claimIndex}`);
      continue;
    }

    const fields = new Set([
      ...Object.keys(currentClaim),
      ...Object.keys(nextClaim),
    ]);
    fields.forEach((field) => {
      if (!isEqual(currentClaim[field], nextClaim[field])) {
        changedFields.push(`claims.${claimIndex}.${field}`);
      }
    });
  }

  return changedFields;
};

const contextualizeViolation = (
  violation: RuleViolation,
  claimIndex: number
): RuleViolation => ({
  ...violation,
  message: `Claim ${claimIndex + 1}: ${violation.message}`,
  formFields: violation.formFields?.map(
    (field) => `claims.${claimIndex}.${field}`
  ),
});

const aggregateEvaluations = (
  evaluations: RuleEvaluationResult[]
): RuleEvaluationResult => {
  const violations = evaluations.flatMap((evaluation, claimIndex) =>
    evaluation.violations.map((violation) =>
      contextualizeViolation(violation, claimIndex)
    )
  );
  const appliedData = evaluations.reduce<Record<string, unknown>>(
    (aggregated, evaluation, claimIndex) => {
      Object.entries(evaluation.appliedData).forEach(([field, value]) => {
        aggregated[`claims.${claimIndex}.${field}`] = value;
      });
      return aggregated;
    },
    {}
  );

  return {
    passed: !violations.some((violation) => violation.severity === 'block'),
    violations,
    appliedData,
    appliedActions: evaluations.flatMap(
      (evaluation) => evaluation.appliedActions
    ),
  };
};

export const useLiveBulkClaimRules = ({
  siteID,
  debounceMilliseconds = 500,
}: {
  siteID: string;
  debounceMilliseconds?: number;
}): LiveBulkClaimRulesState => {
  const isBatchLiveRulesEnabled = useRemoteFeatureFlag(
    'batch_claim_live_rules'
  );
  const isBatchLiveRulesEnabledRef = useRef(isBatchLiveRulesEnabled);
  isBatchLiveRulesEnabledRef.current = isBatchLiveRulesEnabled;
  const [claimsData, setClaimsData] = useState<ClaimRuleFormData[] | null>(
    null
  );
  const [debouncedClaimsData] = useDebouncedValue(
    claimsData,
    debounceMilliseconds
  );
  const [evaluation, setEvaluation] = useState<RuleEvaluationResult | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const latestRequestIDRef = useRef(0);
  const lastScheduledClaimsDataRef = useRef<Array<
    Record<string, unknown>
  > | null>(null);
  const pendingAppliedFieldsRef = useRef<Set<string>>(new Set());

  const setEvaluationBaseline = useCallback(
    (
      formValues: { claims: ClaimRuleFormData[] },
      appliedFields: string[] = []
    ) => {
      const claimsDataSnapshot = cloneDeep(formValues.claims);
      lastScheduledClaimsDataRef.current = claimsDataSnapshot.map((claimData) =>
        normalizeClaimRuleEvaluationData(claimData)
      );
      pendingAppliedFieldsRef.current = new Set(appliedFields);
    },
    []
  );

  const scheduleEvaluation = useCallback(
    (nextClaimsData: ClaimRuleFormData[]) => {
      if (!isBatchLiveRulesEnabledRef.current) return;

      const claimsDataSnapshot = cloneDeep(nextClaimsData);
      const normalizedClaimsData = claimsDataSnapshot.map((claimData) =>
        normalizeClaimRuleEvaluationData(claimData)
      );
      if (isEqual(lastScheduledClaimsDataRef.current, normalizedClaimsData)) {
        return;
      }

      const changedFields = getChangedClaimFields(
        lastScheduledClaimsDataRef.current,
        normalizedClaimsData
      );
      const pendingAppliedFields = pendingAppliedFieldsRef.current;
      pendingAppliedFieldsRef.current = new Set();
      if (
        changedFields.length > 0 &&
        changedFields.every((field) => pendingAppliedFields.has(field))
      ) {
        lastScheduledClaimsDataRef.current = normalizedClaimsData;
        return;
      }

      lastScheduledClaimsDataRef.current = normalizedClaimsData;
      latestRequestIDRef.current += 1;
      setClaimsData(normalizedClaimsData);
      setIsStale(nextClaimsData.length > 0);
      setError(null);
      if (nextClaimsData.length === 0) {
        setEvaluation(null);
        setIsChecking(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isBatchLiveRulesEnabled) return;

    latestRequestIDRef.current += 1;
    lastScheduledClaimsDataRef.current = null;
    pendingAppliedFieldsRef.current = new Set();
    setClaimsData(null);
    setEvaluation(null);
    setError(null);
    setIsChecking(false);
    setIsStale(false);
  }, [isBatchLiveRulesEnabled]);

  useEffect(() => {
    if (!isBatchLiveRulesEnabled || !debouncedClaimsData?.length) return;

    const requestID = ++latestRequestIDRef.current;
    let cancelled = false;
    const isObsoleteRequest = () =>
      cancelled || requestID !== latestRequestIDRef.current;

    const evaluateRules = async () => {
      setIsChecking(true);

      try {
        const evaluations = await Promise.all(
          debouncedClaimsData.map((claimData) =>
            hasOnlyIncompleteTariffs(claimData)
              ? getIncompleteClaimRulesResult()
              : evaluateClaimRules({
                  siteID,
                  claimData: {
                    ...claimData,
                    siteID: claimData.siteID || siteID,
                  },
                })
          )
        );
        const result = aggregateEvaluations(evaluations);

        if (isObsoleteRequest()) return;
        setEvaluation(result);
        setError(null);
        setIsStale(false);
      } catch (caughtError: unknown) {
        if (isObsoleteRequest()) return;
        setEvaluation(null);
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error('Rule evaluation failed.')
        );
        setIsStale(false);
      } finally {
        if (!isObsoleteRequest()) setIsChecking(false);
      }
    };

    void evaluateRules();

    return () => {
      cancelled = true;
    };
  }, [debouncedClaimsData, isBatchLiveRulesEnabled, siteID]);

  return {
    evaluation,
    error,
    isChecking,
    isStale,
    scheduleEvaluation,
    setEvaluationBaseline,
  };
};
